import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { ChatWidgetService } from '@app/shared/services/chat-widget/chat-widget.service';
import { GraphQLService } from '@app/shared/services/graphql/graphql.service';
import { Subscription } from 'rxjs';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { formatDate } from '@angular/common';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
import { validateUserInput, sanitizeMessageContent as sanitizeMessageContentUtil } from '@app/shared/utils';
import { DomSanitizer } from '@angular/platform-browser';

// import { Subscription } from 'rxjs';

@AutoUnsubscribe()
@Component({
  selector: 'explore-with-ai-chat',
  templateUrl: './explore-with-ai-chat.component.html',
  styleUrl: './explore-with-ai-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExploreWithAiChatComponent implements OnInit, OnDestroy, OnChanges {
  @Input() summaryData: any;
  @Input() vault_user_form_id: any;
  @Input() currentTemplate: any;
  @Input() isNonDocumentSummary?: boolean = false;
  @Input() linkedForms: any[] = [];
  @Input() linkDocuments: any[] = [];
  @Input() sessionId: string;
  @ViewChild('chatMessages') private chatMessages: ElementRef;
  groupedMessages: any[] = [];
  loading: boolean = false;
  showSendHoverIcon: boolean = false;
  messageText: string = '';
  currentUser: any;
  currentUserId: string;
  loadOnce: boolean = true;

  lastMessage: any;
  messages: any = [];
  isChatWelcomeMessageLoading: any;
  isShowSummary: any;
  isDocumentForm: any;
  isShowTeachMe = false;
  selectedGuardianUser: string;

  private subscriptions = new Subscription();
  constructor(
    private loginService: LoginStoreEffects,
    private cd: ChangeDetectorRef,
    private chatWidgetService: ChatWidgetService,
    private graphqlService: GraphQLService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['summaryData']) {
      this.groupMessages();
    }
    if (changes['linkedForms']) {
      this.linkedForms = [...changes['linkedForms'].currentValue];
      this.cd.detectChanges();
    }
    if (changes['linkDocuments']) {
      this.linkDocuments = [...changes['linkDocuments'].currentValue];
      this.cd.detectChanges();
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.loginService.loginObject.subscribe((state) => {
        const currentUserId = state.data.id;
        if (currentUserId) {
          this.currentUser = state.data;
          this.selectedGuardianUser = this.currentUser.id;
        }
        if (currentUserId && this.currentUserId !== currentUserId) {
          this.currentUserId = currentUserId;
          this.setupMessageSubscription(currentUserId);
          this.getMessages();
        }
      })
    );
  }

  getMessages() {
    this.loading = true;
    this.subscriptions.add(
      this.chatWidgetService
        .getMessagesBySessionId(this.sessionId)
        .subscribe((res) => {
          const messages = res;
          for (let index = 0; index < messages.length; index++) {
            const element = messages[index];
            const epochDate = element.sk.split('|')[1];
            messages[index].date = new Date(epochDate * 1000);
          }
          this.messages = messages;
          // if(this.messages.length === 0 && !this.summaryData && this.currentTemplate && !this.isNonDocumentSummary) {
          //   this.sendWelcomeMessage();
          // }
          if (this.messages.length) {
            this.isShowTeachMe = false;
          }
          this.groupMessages();
          this.isChatWelcomeMessageLoading = false;
          this.loading = false;
          this.cd.detectChanges();
        })
    );
  }

  // sendWelcomeMessage() {
  //   this.isShowTeachMe = true;
  //   this.cd.detectChanges();
  //   this.subscriptions.add(
  //     this.chatWidgetService.sendMessage({
  //       message: `Greeting: <br/>Hi ${this.currentUser?.nick_name || this.currentUser?.first_name + ' ' + this.currentUser?.last_name}, let me know if there’s anything I can help you with`,
  //       sender: 'SYSTEM',
  //       vault_user_form_id: this.vault_user_form_id,
  //     }).subscribe(() => {})
  //   )
  // }

  showTeachMe() {
    this.subscriptions.add(
      this.chatWidgetService
        .getTipsMessage({
          message: `Teach me about ${this.currentTemplate.type}`,
          sender: 'USER',
          template_type: this.currentTemplate.type,
          vault_user_form_id: this.vault_user_form_id,
        })
        .subscribe(() => {
          this.isShowTeachMe = false;
          this.cd.detectChanges();
        })
    );
  }

  private setupMessageSubscription(userId: string) {
    this.loading = true;
    this.subscriptions.add(
      this.graphqlService.onNewMessage(userId).subscribe({
        next: (response) => {
          if (response.status === 200 && response.data && response.data.session_id === this.sessionId) {
            console.warn('New message received:', response.data);
            const data: any = {
              content: response.data.content,
              sender: response.data.sender,
              sk: response.data.sk,
              pk: response.data.pk,
              session_id: response.data.session_id,
              msg_type: response.data.msg_type,
              vault_user_form_id: JSON.parse(response.data.vault_user_form_id),
            };
            const epochDate = data.sk.split('|')[1];
            data.date = new Date(epochDate * 1000);
            if (!response.data.content.includes('Summary')) {
              this.isShowSummary = true;
            }
            if(response.data.msg_type !== 'VECTOR_CHAT|TEMPLATE|SELECTED') {
              this.loading = false;
            }
            this.addMessage(data);
          }
        },
        error: (error) => {
          console.error('Error in message subscription:', error);
          this.loading = false;
          this.cd.detectChanges();
        },
      })
    );
  }

  groupMessages() {
    const grouped: { [key: string]: any[] } = {};
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    this.lastMessage = this.messages[this.messages.length - 1];
    // this.handleChatState()
    if (this.summaryData) {
      this.summaryData = this.summaryData.replaceAll('<h1>', '<h1 class="text-xl">').replaceAll('<h2>', '<h2 class="text-lg">');
    }
    // for (const msg of this.messages) {
    //   msg.content = msg.content
    //     .replace('<Name>', this.currentUser?.nick_name || this.currentUser?.first_name + ' ' + this.currentUser?.last_name)
    //     .replace('\n', '<br/>')
    //     .replaceAll('<h6>', '<h6 class="my-3">')
    //     .replaceAll('<li>', '<li class="my-1">')
    //     .replaceAll('<code>', '<code class="whitespace-normal break-all wrap-anywhere">')
    //     .replaceAll('*', '')
    //     .replaceAll('#', '')
    //     .replaceAll('<h1>', '<h1 class="text-xl">')
    //     .replaceAll('<h2>', '<h2 class="text-lg">');
    // }

    // Sort messages in ascending order
    this.messages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    for (const msg of this.messages) {
      const msgDate = new Date(msg.date);
      let label = '';

      if (msgDate.toDateString() === today.toDateString()) {
        label = 'Today';
      } else if (msgDate.toDateString() === yesterday.toDateString()) {
        label = 'Yesterday';
      } else {
        label = formatDate(msgDate, 'MMM d, y', 'en-US');
      }

      if (!grouped[label]) {
        grouped[label] = [];
      }

      grouped[label].push(msg);
    }

    // Sort groups by date key (chronologically)
    this.groupedMessages = Object.entries(grouped)
      .map(([label, messages]) => ({ label, messages }))
      .sort((a, b) => {
        const timeA = new Date(a.messages[0].date).getTime();
        const timeB = new Date(b.messages[0].date).getTime();
        return timeA - timeB;
      });
    // if(this.summaryData && this.groupedMessages.length > 0 && this.groupedMessages[0].messages.length && !this.isNonDocumentSummary) {
    //   this.groupedMessages[0].messages.unshift({
    //     content: this.summaryData,
    //     sender: 'SYSTEM',
    //   })
    // } else if(this.summaryData){
    //   this.groupedMessages = [
    //     {
    //       label: 'Today',
    //       messages: [
    //         {
    //           content: this.summaryData,
    //           sender: 'SYSTEM',
    //         }
    //       ]
    //     }
    //   ];
    // }
    this.cd.detectChanges();
    setTimeout(() => this.scrollToBottom(), 0);
  }

  private addMessage(message: any) {
    const messageIndex = this.messages.findIndex((msg: any) => msg.pk === message.pk && msg.sk === message.sk);
    if (messageIndex !== -1) {
      this.messages[messageIndex] = message;
    } else {
      this.messages.push(message);
    }
    // this.store.dispatch(setMessages({ messages: this.messages }));
    this.groupMessages();
    this.cd.detectChanges();
    setTimeout(() => this.scrollToBottom(), 0);
  }

  private scrollToBottom() {
    if (this.chatMessages) {
      const element = this.chatMessages.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  adjustHeight(event: any) {
    if (this.messageText.length > 0) {
      this.showSendHoverIcon = true;
    } else {
      this.showSendHoverIcon = false;
    }
    event.target.style.height = 'auto';
    event.target.style.height = event.target.scrollHeight + 'px';
  }

  /**
   * Sanitizes HTML content to prevent XSS attacks
   * @param content - The HTML content to sanitize
   * @returns Sanitized HTML string safe for rendering
   */
  sanitizeMessageContent(content: string): string {
    return sanitizeMessageContentUtil(content, this.sanitizer);
  }

  sendMessage() {
    if (!this.messageText.trim() || !this.currentUserId) {
      return;
    }

    // Validate and block malicious input
    const validation = validateUserInput(this.messageText.trim());
    
    if (!validation.isValid) {
      // Block the request - show error or clear input
      console.warn('Blocked potentially malicious input');
      this.messageText = '';
      this.cd.detectChanges();
      // Optionally show user-friendly error message
      // You can add a toast notification here if needed
      return;
    }

    this.isShowTeachMe = false;
    this.cd.detectChanges();
    setTimeout(() => this.scrollToBottom(), 0);
    
    // Use sanitized content
    const messageContent = validation.sanitized;
    this.messageText = '';
    this.loading = true;
    // this.subscriptions.add(
    //   this.chatWidgetService
    //     .getSummaryMessage({
    //       message: messageContent,
    //       sender: 'USER',
    //       vault_user_form_id: this.vault_user_form_id,
    //     })
    //     .subscribe(
    //       () => {
    //         this.loading = true;
    //         this.cd.detectChanges();
    //         setTimeout(() => this.scrollToBottom(), 0);
    //       },
    //       () => {
    //         this.loading = false;
    //         this.cd.detectChanges();
    //         setTimeout(() => this.scrollToBottom(), 0);
    //       }
    //     )
    // );
    this.subscriptions.add(
      this.chatWidgetService
        .sendMessage({ 
          content: messageContent, 
          session_id: this.sessionId, 
          sender: 'USER', msg_type: 'VECTOR_CHAT|TEMPLATE|SELECTED', 
          g_user_id: this.selectedGuardianUser !== this.currentUser.id ? this.selectedGuardianUser : null, 
          vault_user_form_id: JSON.stringify({
            id: this.vault_user_form_id,
            linkedForms: this.linkedForms.map((form: any) => form.id),
            linkDocuments: this.linkDocuments.map((document: any) => document.id),
          }) 
        })
        .subscribe(() => {
          // Ensure scroll after message is sent
          setTimeout(() => {
            this.scrollToBottom();
          }, 100);
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
